import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import type { VendorMatch } from '../../types/vendor';

interface VendorListProps {
  vendors: VendorMatch[];
  selectedVendor: string | null;
  onSelectVendor: (vendorId: string) => void;
}

const getMatchColor = (score: number) => {
  if (score >= 90) return 'success';
  if (score >= 75) return 'warning';
  return 'default';
};

const getMatchBgColor = (score: number) => {
  if (score >= 90) return alpha('#4caf50', 0.06);
  if (score >= 75) return alpha('#ff9800', 0.06);
  return alpha('#9e9e9e', 0.04);
};

const VendorList: React.FC<VendorListProps> = ({
  vendors,
  selectedVendor,
  onSelectVendor,
}) => {
  return (
    <Stack
      spacing={0.75}
      sx={{
        maxHeight: 180,
        overflowY: 'auto',
        pr: 0.5,
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-track': {
          bgcolor: 'grey.100',
          borderRadius: 3,
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'grey.400',
          borderRadius: 3,
          '&:hover': {
            bgcolor: 'grey.500',
          },
        },
      }}>
      {vendors.map((vendor: VendorMatch) => (
        <Card
          key={vendor.vendorId}
          variant="outlined"
          sx={{
            cursor: 'pointer',
            flexShrink: 0,
            borderColor:
              selectedVendor === vendor.vendorId ? 'primary.main' : 'divider',
            borderWidth: selectedVendor === vendor.vendorId ? 2 : 1,
            bgcolor:
              selectedVendor === vendor.vendorId
                ? alpha('#1976d2', 0.04)
                : getMatchBgColor(vendor.similarityScore),
            transition: 'all 0.15s ease',
            '&:hover': {
              borderColor: 'primary.light',
              bgcolor: alpha('#1976d2', 0.06),
            },
          }}
          onClick={e => {
            e.stopPropagation();
            onSelectVendor(vendor.vendorId);
          }}>
          <CardContent
            sx={{
              py: 0.75,
              px: 1.5,
              '&:last-child': { pb: 0.75 },
            }}>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between">
              <Stack
                direction="row"
                alignItems="flex-start"
                spacing={1}
                sx={{ flex: 1, minWidth: 0 }}>
                {selectedVendor === vendor.vendorId ? (
                  <CheckCircleIcon
                    color="primary"
                    sx={{ fontSize: 18, mt: 0.25 }}
                  />
                ) : (
                  <StoreIcon
                    color="action"
                    sx={{ fontSize: 18, opacity: 0.5, mt: 0.25 }}
                  />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    fontWeight="medium"
                    fontSize="0.85rem"
                    lineHeight={1.3}
                    sx={{
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}>
                    {vendor.vendorName}
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {vendor.companyName && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontSize="0.7rem">
                        {vendor.companyName}
                      </Typography>
                    )}
                    {vendor.vendorGST && (
                      <>
                        {vendor.companyName && (
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            fontSize="0.7rem">
                            •
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontSize="0.7rem">
                          {vendor.vendorGST}
                        </Typography>
                      </>
                    )}
                  </Stack>
                </Box>
              </Stack>
              <Chip
                label={`${vendor.similarityScore}%`}
                size="small"
                color={getMatchColor(vendor.similarityScore)}
                sx={{
                  fontWeight: 'bold',
                  minWidth: 44,
                  height: 22,
                  fontSize: '0.7rem',
                  ml: 1,
                  flexShrink: 0,
                }}
              />
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
};

export default VendorList;
